import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Search, BookOpen, Tag } from "lucide-react";
import { KNOWLEDGE_ARTICLES } from "@/data";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.25, 0.46, 0.45, 0.94] } },
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.08 } } };

const CATEGORIES = ["全部", "呼吸與正念", "靜觀冥想", "情緒管理", "瑜伽哲學", "解剖學", "樂齡健康"];

const categoryColors: Record<string, string> = {
  "呼吸與正念": "bg-sky-100 text-sky-800",
  "靜觀冥想": "bg-purple-100 text-purple-800",
  "情緒管理": "bg-rose-100 text-rose-800",
  "瑜伽哲學": "bg-amber-100 text-amber-800",
  "解剖學": "bg-emerald-100 text-emerald-800",
  "樂齡健康": "bg-orange-100 text-orange-800",
};

export default function Knowledge() {
  const [activeCategory, setActiveCategory] = useState("全部");
  const [searchQuery, setSearchQuery] = useState("");

  const filtered = KNOWLEDGE_ARTICLES.filter((a) => {
    const matchCat = activeCategory === "全部" || a.category === activeCategory;
    const matchSearch = !searchQuery ||
      a.title.includes(searchQuery) ||
      a.summary.includes(searchQuery) ||
      a.tags.some((t) => t.includes(searchQuery));
    return matchCat && matchSearch;
  });

  return (
    <div className="bg-[#fdfbf7] overflow-x-hidden">
      {/* ── HERO ─────────────────────────────────────── */}
      <section className="relative py-36 bg-gradient-to-br from-[#2c1f0e] to-[#3d2b1a] overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: "radial-gradient(circle at 20% 50%, #c8a96e 0%, transparent 50%), radial-gradient(circle at 80% 20%, #c8a96e 0%, transparent 40%)"
        }} />
        <div className="relative z-10 container mx-auto px-6 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl mx-auto">
            <motion.p variants={fadeUp} className="text-[#c8a96e] text-xs tracking-[0.4em] uppercase mb-4">
              免費知識庫
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-serif text-5xl md:text-6xl text-[#fdfbf7] mb-6">
              瑜伽知識庫
            </motion.h1>
            <motion.p variants={fadeUp} className="text-[#f0e6d3]/70 text-lg leading-relaxed mb-8 max-w-xl mx-auto">
              哲學冷知識 · 解剖學入門 · 情緒與身體 · 修行日記
              <br />
              <span className="text-[#c8a96e]/80 text-sm">免費開放閱讀 · SEO 優化內容持續更新</span>
            </motion.p>
            {/* Search */}
            <motion.div variants={fadeUp} className="relative max-w-md mx-auto">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8a7060]" />
              <input
                type="text"
                placeholder="搜尋文章、標籤……"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-white/10 border border-[#c8a96e]/30 text-[#fdfbf7] placeholder-[#f0e6d3]/40 rounded-full px-10 py-3 text-sm focus:outline-none focus:border-[#c8a96e]/60 backdrop-blur-sm"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── CATEGORY FILTER ─────────────────────────── */}
      <section className="bg-white border-b border-[#c8a96e]/15 sticky top-16 z-40">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center gap-3 overflow-x-auto scrollbar-none">
            <Tag size={14} className="text-[#8a7060] flex-shrink-0" />
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`flex-shrink-0 px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                  activeCategory === cat
                    ? "bg-[#2c1f0e] text-[#c8a96e]"
                    : "bg-[#f5f0e8] text-[#5c4033] hover:bg-[#eae4d8]"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── ARTICLES GRID ─────────────────────────────── */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="max-w-6xl mx-auto"
          >
            {filtered.length === 0 ? (
              <div className="text-center py-20 text-[#8a7060]">
                <BookOpen size={40} className="mx-auto mb-4 opacity-40" />
                <p>找不到相關文章，請嘗試其他關鍵字。</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filtered.map((article) => (
                  <motion.article
                    key={article.id}
                    variants={fadeUp}
                    className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col"
                    style={{ boxShadow: "0 4px 20px -4px rgba(44,31,14,0.08)" }}
                  >
                    {/* Thumbnail */}
                    <div className="relative h-52 overflow-hidden bg-[#f5f0e8]">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#2c1f0e]/30 to-transparent" />
                      <span className={`absolute top-3 left-3 text-xs font-semibold px-3 py-1 rounded-full ${categoryColors[article.category] || "bg-gray-100 text-gray-700"}`}>
                        {article.category}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="p-6 flex flex-col flex-1">
                      <h2 className="font-serif text-lg text-[#2c1f0e] mb-1 leading-snug group-hover:text-[#c8a96e] transition-colors">
                        {article.title}
                      </h2>
                      <p className="text-[#8a7060] text-xs italic mb-3">{article.titleEn}</p>
                      <p className="text-[#5c4033]/75 text-sm leading-relaxed mb-4 flex-1">{article.summary}</p>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {article.tags.map((tag) => (
                          <span
                            key={tag}
                            onClick={() => setSearchQuery(tag)}
                            className="text-xs px-2.5 py-1 bg-[#f5f0e8] text-[#8a7060] rounded-full cursor-pointer hover:bg-[#c8a96e]/20 hover:text-[#2c1f0e] transition-colors"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>

                      <button className="inline-flex items-center gap-1.5 text-[#2c1f0e] text-sm font-semibold border-b border-[#c8a96e] pb-0.5 w-fit hover:text-[#c8a96e] transition-colors">
                        閱讀全文 <ArrowRight size={14} />
                      </button>
                    </div>
                  </motion.article>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* ── CONTRIBUTE INVITE ────────────────────────── */}
      <section className="py-16 bg-[#f5f0e8]">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-3xl mx-auto text-center"
          >
            <div className="inline-block text-4xl mb-4">✍️</div>
            <h2 className="font-serif text-2xl text-[#2c1f0e] mb-4">更多文章持續更新中</h2>
            <p className="text-[#5c4033]/70 leading-relaxed mb-6 max-w-xl mx-auto">
              這個知識庫正在建設中。Virginia 會持續將她的教學心得、課堂筆記和瑜伽冷知識整理成文章，免費與大家分享。
            </p>
            <p className="text-[#8a7060] text-sm mb-6">想第一時間收到新文章通知？在 Instagram 關注我們。</p>
            <div className="flex flex-wrap justify-center gap-3">
              <a
                href="https://instagram.com/abhyaasyoga"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#2c1f0e] text-[#c8a96e] px-7 py-3 rounded-full font-semibold text-sm hover:bg-[#3d2b1a] transition-colors"
              >
                @abhyaasyoga on Instagram <ArrowRight size={14} />
              </a>
              <a
                href="/courses"
                className="inline-flex items-center gap-2 border border-[#2c1f0e] text-[#2c1f0e] px-7 py-3 rounded-full font-semibold text-sm hover:bg-[#2c1f0e] hover:text-[#c8a96e] transition-colors"
              >
                探索師資培訓課程 <ArrowRight size={14} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── SEO: POPULAR TOPICS ─────────────────────── */}
      <section className="py-14 bg-[#2c1f0e]">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="max-w-4xl mx-auto text-center"
          >
            <motion.p variants={fadeUp} className="text-[#c8a96e] text-xs tracking-widest uppercase mb-6">熱門主題</motion.p>
            <motion.div variants={stagger} className="flex flex-wrap justify-center gap-3">
              {["瑜伽哲學", "情緒管理", "呼吸法", "陰瑜伽", "筋膜", "瑜伽經", "正念靜觀", "長者瑜伽", "產前瑜伽", "體式調整", "解剖學", "冥想技巧", "Patanjali", "心念控制", "師資培訓"].map((topic) => (
                <motion.button
                  key={topic}
                  variants={fadeUp}
                  onClick={() => { setSearchQuery(topic); window.scrollTo({ top: 400, behavior: "smooth" }); }}
                  className="px-4 py-2 bg-[#3d2b1a]/60 border border-[#c8a96e]/20 text-[#f0e6d3]/70 text-sm rounded-full hover:border-[#c8a96e]/50 hover:text-[#c8a96e] transition-all cursor-pointer"
                >
                  {topic}
                </motion.button>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
